import { GiftShippingAddressInput, ReferralSource, Species } from './gql-op-types';
import { CouponDiscount, FiCurrency } from './index';

/**
 * -- Checkout types --
 * store: main e-commerce store
 * upgrade: upgrade to the latest collar model (in-app webview)
 * subscription: purchase a subscription for a collar that currently does not have a subscription (in-app webview)
 * membership_upgrade: change the plan on an existing subscription already attached to a collar (in-app webview)
 * monthly_upgrade: change the plan on an existing S3 monthly subscription already attached to a collar (in-app webview). Special promotion
 */
export enum CheckoutType {
  Store = 'store',
  Upgrade = 'upgrade',
  Subscription = 'subscription',
  MembershipUpgrade = 'membership_upgrade',
  MonthlyUpgrade = 'monthly_upgrade',
  Verizon = 'verizon',
}

// Cart pricing
export interface SerializedCartValidationError {
  code: string;
  message: string;
}

export interface CartPricingValidationErrors {
  couponCode?: SerializedCartValidationError | null;
  referralCode?: SerializedCartValidationError | null;
  redeemedGiftCardCode?: SerializedCartValidationError | null;
}

export type CartItemCreditReason =
  | 'upgrade'
  | 'promo'
  | 'promo_waived_activation_fee'
  | 'household_discount_five_percent'
  | 'household_discount_ten_percent'
  | 'free_trial';

export interface CartItemCredit {
  creditReason: CartItemCreditReason;
  creditAmountInCents: number;
}

export interface RecurringCharge {
  amountInCents: number;
  interval: 'year' | 'month' | 'week' | 'day';
  intervalCount: number;
  quantity: number;
  freeTrialDays?: number;
}

export interface PositionDiscountPreview {
  position: number;
  discountPercent: number;
  isEnrolled: boolean;
}

/** strikethrough display data for a hardware-first repriced cart item; both savings are already in the unit price */
export interface HardwareDiscountPreview {
  listPriceInCents: number;
  discountedPriceInCents: number;
  /** the promo code's share of the saving; 0 without a recognized code */
  promoDiscountInCents: number;
  /** the flat per-tracker saving once the cart holds 2+; 0 while the cart holds a single tracker */
  multiDeviceDiscountInCents: number;
}

export interface CartPricing {
  couponDiscount?: CouponDiscount | null;
  validationErrors?: CartPricingValidationErrors;
  activationFeesInCents?: number;
  activationFeeInCents?: number;
  subtotalInCents: number;
  appliedDiscountInCents: number;
  upgradeCreditAmountInCents?: number;
  cartItemCredits?: {
    [cartItemId: string]: CartItemCredit[];
  };
  positionDiscounts?: {
    [cartItemId: string]: PositionDiscountPreview;
  };
  hardwareDiscounts?: {
    [cartItemId: string]: HardwareDiscountPreview;
  };
  /** hardware-first: active member, or a membership in the cart; keeps klarna off the cart, pricing ignores it */
  hardwareFirstExistingMember?: boolean;
  /** hardware-first: the multi-device saving summed over the cart, already in the unit prices */
  hardwareFirstMultiDeviceDiscountInCents?: number;
  totalInCents: number;
  totalValueOfRedeemedGiftCardInCents?: number;
  appliedGiftCardAmountInCents?: number;
  appliedAccountBalanceInCents?: number;
  shippingInCents?: number;
  taxInCents?: number;
  referrerName?: string;
  referrerAvatarUrl?: string;
  referrerPetAvatarUrl?: string;
  referreeEligibleForFreeMonth?: boolean;
  referreeEligibleForCartDiscount?: boolean;
  totalRecurringCharges?: RecurringCharge[];
  isUpgradeV2?: boolean;
  waivedActivationFeesInCents?: number;
  /** Currency code from billing service (e.g., 'USD', 'CAD') */
  currency?: string;
}

// Cart items
export interface GiftCardDelivery {
  deliverAt?: DateTime | null;
  emailAddress: string;
  firstName?: string | null;
  lastName?: string | null;
  gifterName?: string | null;
  personalMessage?: string | null;
}

export interface LineItem {
  lineItemId: string;
  sku: string;
}

export interface SingleLineCartItem {
  cartItemId: string;
  lineItem: LineItem;
  quantity: number;
}

/**
 * This cart item is associated with a specific module id. Used in a couple cases:
 *   1) to specify the module that's being upgraded
 *   2) to specify the module that should be assigned the newly purchased subscription
 */
export interface ForModuleCartItem extends SingleLineCartItem {
  forModuleId: string;
}

export interface BundleCartItem extends SingleLineCartItem {
  subscriptionLineItem: LineItem;
  addonLineItems?: LineItem[];
  isGift: boolean;
  /** Client-side only; not included in the purchase GraphQL input mapping (backend ignores this field). Tracks which species the user selected on the PDP. */
  species?: Species;
}

export interface ForModuleBundleCartItem extends BundleCartItem {
  forModuleId: string;
}

export interface GiftCardCartItem extends SingleLineCartItem {
  giftCardDelivery: GiftCardDelivery;
}

export type StoreCartItem = SingleLineCartItem | BundleCartItem | GiftCardCartItem;
export type Series3UpgradeCartItem = ForModuleBundleCartItem;
export type SubscriptionCartItem = ForModuleCartItem;

export type CartItem = StoreCartItem | Series3UpgradeCartItem | SubscriptionCartItem;

/**
 * Defines a cart for the main e-commerce store. Other carts include: upgrade, subscription
 */
export interface StoreCart {
  cartItems: { [key: string]: StoreCartItem };
  redeemedGiftCardCode?: string;
  couponCode?: string;
  /** hardware-first: the shopper marked the order as a gift. sent to web with the purchase. */
  isGift?: boolean;
  referralCode?: string;
  referralSource?: ReferralSource;
  referrerName?: string;
  referrerAvatarUrl?: string;
  referrerPetAvatarUrl?: string;
  giftShippingAddress?: GiftShippingAddressInput;
  currency: FiCurrency;
  containsGiftItems?: boolean;
  containsAllGiftItems?: boolean;
  awinClick?: string;
  facebookClick?: string;
  showInAppCheckout?: boolean;
}

export interface Series3UpgradeCart {
  cartItems: { [key: string]: Series3UpgradeCartItem };
  // Need to add these properties to the upgrade cart to simplify some reuseable code that operates on a Cart type
  // which is a union of cart types. But for the S3 upgrade cart we do not allow for adding any codes to the cart so
  // they will always be undefined.
  redeemedGiftCardCode?: undefined;
  couponCode?: undefined;
  isGift?: undefined;
  referralCode?: undefined;
  referrerName?: string;
  giftShippingAddress?: GiftShippingAddressInput;
  currency: FiCurrency;
}

/**
 * A cart where the cart items will be for purchasing subscriptions for specific modules. These carts do not allow
 * promo codes.
 */
export interface SubscriptionCart {
  cartItems: { [key: string]: SubscriptionCartItem };
  redeemedGiftCardCode?: string;
  couponCode?: string;
  isGift?: undefined;
  referralCode?: undefined;
  referrerName?: string;
  giftShippingAddress?: GiftShippingAddressInput;
  currency: FiCurrency;
}

export type Cart = StoreCart | Series3UpgradeCart | SubscriptionCart;
